import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Switch,
  createTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Drawer,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMainContentLogic } from './SanskritMainContentLogics';
import '../sanskritContent/styles/App.css';
import './fonts/siddhanta.ttf';
import { Link } from 'react-router-dom';
import { Button } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';

interface Shloka {
  shloka: string[];
  Authourline: string; 
  taranga_heading: string;
  mangalacharana: string;
}

interface Taranga {
  taranga: Shloka[];
  lambaka_heading: string;
}

interface Data {
  body: {
    page: {
      text: string;
      lambaka: Taranga[];
    };
  };
}

const SanskritMainContent: React.FC = () => {
  const {
    data,
    error,
    open,
    darkMode,
    currentTaranga,
    handleDrawerToggle,
    handleThemeChange,
    handleTarangaNavigation,
  } = useMainContentLogic();

  const [expandedLambaka, setExpandedLambaka] = useState<number | null>(null);
  const [currentLambakaIndex, setCurrentLambakaIndex] = useState<number | null>(null);
  const [selectedTarangaIndex, setSelectedTarangaIndex] = useState<number | null>(null); // State for the selected Taranga

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
    },
    typography: {
      fontFamily: 'Siddhanta, Roboto, Helvetica, Arial, sans-serif',
    },
  });

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const getTitleFontSize = () => {
    if (isSmallScreen) return '6vw';
    if (isMediumScreen) return '4vw';
    if (isLargeScreen) return '3vw';
    return '2.5vw';
  };

  const handleLambakaAccordionChange = (index: number) => {
    setExpandedLambaka(expandedLambaka === index ? null : index);
  };

  const handleTarangaClick = (lambakaIndex: number, tarangaIndex: number) => {
    handleTarangaNavigation(tarangaIndex);
    setCurrentLambakaIndex(lambakaIndex); // Set the current lambaka index
    setSelectedTarangaIndex(tarangaIndex); // Set the selected Taranga index
    handleDrawerToggle();
  };

  // Automatically select the first Taranga of the first Lambaka when data is available
  useEffect(() => {
    if (data?.body?.page?.lambaka && data.body.page.lambaka.length > 0) {
      setCurrentLambakaIndex(0); // Set the first lambaka as the current one
      setSelectedTarangaIndex(0); // Set the first taranga of the first lambaka as selected
    }
  }, [data]); // Dependency on data to ensure it runs when data is fetched

  // Log data to ensure it is fetched properly
  console.log('Data:', data);
  console.log('Current Taranga:', currentTaranga);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Katha Saritsagara
          </Typography>
          <Switch checked={darkMode} onChange={handleThemeChange} color="default" />
          <Link to="/">
          <button className="homeBtn" style={{ display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center"}}>
            <HomeIcon style={{ display:"flex",  fontSize:"31px" }} /> {/* This is the HomeIcon */}
            <span style={{display:"block", fontSize:"10px"}}>Home</span>
          </button>
          </Link>
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={open} onClose={handleDrawerToggle}>
      <div className='drawerHeading'>Explore Books</div>
        <List>
          {data?.body?.page?.lambaka &&
            data.body.page.lambaka.map((lambaka: Taranga, lambakaIndex: number) => (
              <div key={lambakaIndex}>
                <Accordion
                  expanded={expandedLambaka === lambakaIndex}
                  onChange={() => handleLambakaAccordionChange(lambakaIndex)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{lambaka.lambaka_heading}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {lambaka.taranga &&
                        lambaka.taranga.map((taranga: Shloka, tarangaIndex: number) => (
                          <ListItem
                            key={tarangaIndex}
                            button
                            selected={selectedTarangaIndex === tarangaIndex && currentLambakaIndex === lambakaIndex} // Check both lambaka and taranga index
                            onClick={() => handleTarangaClick(lambakaIndex, tarangaIndex)} // Pass both indices
                          >
                            <ListItemText primary={taranga.taranga_heading} />
                          </ListItem>
                        ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Divider />
              </div>
            ))}
        </List>
      </Drawer>
     
      <Container maxWidth="lg" style={{ height: '100vh', overflow: 'hidden' }}>
  {/* Sticky Header */}
  <div className="StickyHeader">
    <Typography
      variant="h2"
      gutterBottom
      className="Title"
      style={{ fontSize: getTitleFontSize() }}
    >
      {data?.body?.page?.data_title || 'Loading...'}
    </Typography>
    <Typography
      variant="h6"
      gutterBottom
      className="Title"
      style={{ fontSize: '25px', marginBottom: '-0.65em' }}
    >
      {data?.body?.page?.mangalacharana
        ? data.body.page.mangalacharana.split('\n').map((line: number, index: number) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))
        : 'Loading...'}
    </Typography>
  </div>

  {/* Scrollable Content */}
  <div >
    {/* Display Error */}
    {error && <Typography variant="body1" color="error">{error}</Typography>}
    {/* Loading Message */}
    {!error && !data && <Typography variant="body1">Loading data...</Typography>}
    {/* Display Content if available */}
    {currentLambakaIndex !== null &&
      data?.body?.page?.lambaka &&
      data.body.page.lambaka.length > 0 && (
        <Card className="Section" variant="outlined" style={{ width: '100%', margin:"auto" }}>
          <CardContent>
            {/* Render lambaka heading */}
            <Typography variant="h4" gutterBottom className="LambakaHeading">
              {data?.body?.page?.lambaka?.[currentLambakaIndex]?.lambaka_heading ||
                'Select a Taranga'}
            </Typography>

            {/* Render Taranga details if available */}
            {selectedTarangaIndex !== null &&
              data?.body?.page?.lambaka?.[currentLambakaIndex]?.taranga &&
              data.body.page.lambaka[currentLambakaIndex].taranga.map(
                (tarangaItem: Shloka, index: number) => {
                  if (index === selectedTarangaIndex) {
                    return (
                      <div key={index} style={{ marginBottom: '20px' }}>
                        {/* Taranga Heading */}
                        <Typography variant="h5" className="TarangaHeading">
                          {tarangaItem.taranga_heading}
                        </Typography>

                        {/* Shloka List */}
                        <List className="ScrollableContent">
                          {tarangaItem.shloka.map((shlokaText, shlokaIndex) => (
                            <ListItem key={shlokaIndex}>
                              <ListItemText primary={shlokaText} />
                            </ListItem>
                          ))}

                          {/* Authorline */}
                          <Typography variant="body2" className="AuthorLine">
                            {tarangaItem.Authourline}
                          </Typography>
                        </List>
                      </div>
                    );
                  }
                  return null;
                }
              )}
          </CardContent>
        </Card>
      )}
  </div>
</Container>

    </ThemeProvider>
  );
};

export default SanskritMainContent;
